/**
 * "Ask" as the pane holds it: a conversation that the server keeps, and whose
 * answers are worked out in a run in the server. The pane starts a run, asks
 * how far it got, and reads the conversation back when the run is over. This
 * file is the part with no React and no network in it: the shapes, and how the
 * server's turns become what the pane draws.
 */
import type { AskMarkdownSource } from '../../ui/components/AskMarkdown';

export type AskActivityCluster = { stage: string; lines: string[] };

/** What the agent did while it answered one question, stage by stage. */
export type AskActivity = {
  kind: string;
  /** `stopped`: the lawyer stopped it, or the run was cut off. */
  state: 'running' | 'done' | 'failed' | 'stopped';
  clusters: AskActivityCluster[];
};

/** The run that is going on in the server; null when none is. */
export type AskRun = { kind: string; activity: AskActivity | null; stop_requested: boolean } | null;

export type AskRunStatus = { running: boolean; run: AskRun };

/** A turn of the conversation as the server returns it (`GET /chat/sessions/{id}`). */
export type AskServerMessage = {
  id: string;
  content: string;
  is_user: boolean;
  metadata?: {
    kind?: string;
    activity?: AskActivity;
    notice?: 'stopped' | 'failed';
    status?: string;
    verified?: boolean;
    attachments?: Array<{ file_id?: string | null; filename: string; size?: number | null }>;
  } | null;
  /** `text` is the words the answer quotes; `full_text` the passage they are in. */
  sources?: Array<{
    chunk_index?: number | string | null;
    filename?: string;
    location?: string;
    text?: string;
    full_text?: string;
  }> | null;
};

/** A saved "Ask" conversation, as the list says it. */
export type AskConversationMeta = {
  id: string;
  title: string;
  /** The Word file the conversation was held on; empty when the server was not told. */
  documentName: string;
  updatedAt: number;
  running: boolean;
};

export type AskTurn = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** The files the lawyer's turn came with, by name. */
  attachments?: string[];
  /** A finished run's steps: the turn is drawn as the folded line of what was done. */
  activity?: AskActivity;
  /** The turn says how a run ended when it did not end with an answer. */
  notice?: 'stopped' | 'failed';
  /** The sources an answer points at as [1], [2]. */
  sources?: AskMarkdownSource[];
};

/** The words the activity line is said in. */
export type AskActivityCopy = {
  starting: string;
  done: (steps: number) => string;
  failed: string;
  stopped: string;
  stage: (stage: string) => string;
};

/** A line that says something is under way ("קורא את…"), as opposed to one that reports a result. */
function isUnderWay(line: string): boolean {
  return /(…|\.\.\.)$/.test(line.trim());
}

/** How many things the agent did in a run: every reported line of every stage. */
export function activitySteps(activity: AskActivity): number {
  return activity.clusters.reduce((sum, cluster) => sum + cluster.lines.filter((line) => !isUnderWay(line)).length, 0) ||
    activity.clusters.reduce((sum, cluster) => sum + cluster.lines.length, 0);
}

/** One line for a whole run. While it runs: what it is doing now. When it is over: what was done. */
export function activitySummary(activity: AskActivity, copy: AskActivityCopy): string {
  const current = activity.clusters[activity.clusters.length - 1];
  if (activity.state === 'running') {
    return current && current.lines.length > 0 ? current.lines[current.lines.length - 1] : copy.starting;
  }
  if (activity.state === 'failed') return copy.failed;
  if (activity.state === 'stopped') return copy.stopped;
  return copy.done(activitySteps(activity));
}

/** The sources of an answer, each under the number the answer points at it with. */
export function sourcesOf(message: AskServerMessage): AskMarkdownSource[] {
  const out: AskMarkdownSource[] = [];
  for (const source of message.sources ?? []) {
    const n = Number(source.chunk_index);
    if (!Number.isInteger(n) || n < 1 || !source.filename || out.some((item) => item.n === n)) continue;
    out.push({
      n,
      filename: source.filename,
      location: source.location || undefined,
      quote: source.text || undefined,
      fullText: source.full_text || undefined,
    });
  }
  return out.sort((a, b) => a.n - b.n);
}

/** The conversation's turns as the pane draws them. A turn with nothing to draw is left out. */
export function turnsFromServer(messages: AskServerMessage[]): AskTurn[] {
  const turns: AskTurn[] = [];
  for (const message of messages) {
    const meta = message.metadata ?? {};
    if (meta.kind === 'activity') {
      if (meta.activity && meta.activity.clusters?.length) {
        turns.push({ id: message.id, role: 'assistant', content: '', activity: meta.activity });
      }
      continue;
    }
    const content = (message.content ?? '').trim();
    const attachments = (meta.attachments ?? []).map((file) => file.filename).filter(Boolean);
    if (!content && attachments.length === 0) continue;
    if (message.is_user) {
      turns.push({ id: message.id, role: 'user', content, attachments: attachments.length ? attachments : undefined });
      continue;
    }
    const sources = sourcesOf(message);
    turns.push({
      id: message.id,
      role: 'assistant',
      content,
      notice: meta.kind === 'notice' ? meta.notice : undefined,
      sources: sources.length ? sources : undefined,
    });
  }
  return turns;
}

/** The server's clock is UTC and says so only when asked. */
export function serverTime(value: string): number {
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
}
